import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Eye, Save } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function BlogAdmin() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    category: 'product-updates',
    featured_image: '',
    status: 'draft'
  });

  useEffect(() => {
    checkAuth();
    loadPosts();
  }, []);

  const checkAuth = () => {
    const token = sessionStorage.getItem('admin_session_token');
    if (!token) {
      toast.error('Admin access required');
      navigate('/admin/login');
    }
  };

  const loadPosts = async () => {
    try {
      const token = sessionStorage.getItem('admin_session_token');
      const response = await fetch(`${BACKEND_URL}/api/blog/admin/posts`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setPosts(data.posts || []);
    } catch (error) {
      console.error('Failed to load posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSavePost = async () => {
    try {
      const token = sessionStorage.getItem('admin_session_token');
      const url = editingPost 
        ? `${BACKEND_URL}/api/blog/admin/posts/${editingPost._id}`
        : `${BACKEND_URL}/api/blog/admin/posts`;
      
      const method = editingPost ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingPost ? 'Post updated!' : 'Post created!');
        setShowEditor(false);
        setEditingPost(null);
        setFormData({ title: '', content: '', excerpt: '', category: 'product-updates', featured_image: '', status: 'draft' });
        await loadPosts();
      } else {
        throw new Error('Failed to save post');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save post');
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      const token = sessionStorage.getItem('admin_session_token');
      const response = await fetch(`${BACKEND_URL}/api/blog/admin/posts/${postId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success('Post deleted');
        await loadPosts();
      } else {
        throw new Error('Failed to delete');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete post');
    }
  };

  const handleEditPost = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      content: post.content,
      excerpt: post.excerpt || '',
      category: post.category,
      featured_image: post.featured_image || '',
      status: post.status
    });
    setShowEditor(true);
  };

  return (
    <div className="min-h-screen" style={{background: '#0B1120'}}>
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Blog Management</h1>
            <p className="text-[#9CB0C7]">Create and manage blog posts</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/admin/super')}
              className="px-4 py-2 border border-[#22304A] text-[#9CB0C7] rounded-lg hover:bg-[#1A2438] transition-colors"
            >
              Back to Dashboard
            </button>
            <button
              onClick={() => {
                setEditingPost(null);
                setFormData({ title: '', content: '', excerpt: '', category: 'product-updates', featured_image: '', status: 'draft' });
                setShowEditor(true);
              }}
              className="px-4 py-2 bg-gradient-to-r from-[#3EF1C7] to-[#7A5CFF] text-white rounded-lg hover:opacity-90 transition-opacity font-semibold flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              New Post
            </button>
          </div>
        </div>

        {showEditor ? (
          /* Editor */
          <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-6 mb-8">
            <h2 className="text-xl font-bold text-white mb-6">
              {editingPost ? 'Edit Post' : 'New Post'}
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#9CB0C7] mb-2">Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="w-full px-4 py-3 rounded-lg bg-[#111A2E] border border-[#22304A] text-white focus:ring-2 focus:ring-[#3EF1C7] focus:border-transparent"
                  placeholder="Post title..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#9CB0C7] mb-2">Excerpt</label>
                <textarea
                  value={formData.excerpt}
                  onChange={(e) => setFormData({...formData, excerpt: e.target.value})}
                  rows={2}
                  className="w-full px-4 py-3 rounded-lg bg-[#111A2E] border border-[#22304A] text-white focus:ring-2 focus:ring-[#3EF1C7] focus:border-transparent"
                  placeholder="Brief summary (optional)..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#9CB0C7] mb-2">Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className="w-full px-4 py-3 rounded-lg bg-[#111A2E] border border-[#22304A] text-white focus:ring-2 focus:ring-[#3EF1C7] focus:border-transparent"
                  >
                    <option value="product-updates">Product Updates</option>
                    <option value="guides">Guides & Tutorials</option>
                    <option value="case-studies">Case Studies</option>
                    <option value="industry-news">Industry News</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#9CB0C7] mb-2">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    className="w-full px-4 py-3 rounded-lg bg-[#111A2E] border border-[#22304A] text-white focus:ring-2 focus:ring-[#3EF1C7] focus:border-transparent"
                  >
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#9CB0C7] mb-2">Featured Image URL</label>
                <input
                  type="url"
                  value={formData.featured_image}
                  onChange={(e) => setFormData({...formData, featured_image: e.target.value})}
                  className="w-full px-4 py-3 rounded-lg bg-[#111A2E] border border-[#22304A] text-white focus:ring-2 focus:ring-[#3EF1C7] focus:border-transparent"
                  placeholder="https://..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#9CB0C7] mb-2">Content (Markdown supported)</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({...formData, content: e.target.value})}
                  rows={12}
                  className="w-full px-4 py-3 rounded-lg bg-[#111A2E] border border-[#22304A] text-white font-mono text-sm focus:ring-2 focus:ring-[#3EF1C7] focus:border-transparent"
                  placeholder="Write your post content here..."
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleSavePost}
                  className="px-6 py-3 bg-gradient-to-r from-[#3EF1C7] to-[#7A5CFF] text-white rounded-lg hover:opacity-90 transition-opacity font-semibold flex items-center gap-2"
                >
                  <Save className="w-5 h-5" />
                  {editingPost ? 'Update Post' : 'Create Post'}
                </button>
                <button
                  onClick={() => {
                    setShowEditor(false);
                    setEditingPost(null);
                  }}
                  className="px-6 py-3 border border-[#22304A] text-[#9CB0C7] rounded-lg hover:bg-[#1A2438] transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        ) : (
          /* Posts List */
          <div className="space-y-4">
            {posts.map((post) => (
              <div
                key={post._id}
                className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-6 flex items-center justify-between"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-white">{post.title}</h3>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      post.status === 'published' 
                        ? 'bg-green-500/10 text-green-500'
                        : 'bg-yellow-500/10 text-yellow-500'
                    }`}>
                      {post.status}
                    </span>
                  </div>
                  <p className="text-sm text-[#9CB0C7] mb-2">
                    {post.excerpt || post.content?.substring(0, 100) + '...'}
                  </p>
                  <div className="flex items-center gap-4 text-xs text-[#9CB0C7]">
                    <span>{post.category}</span>
                    <span>•</span>
                    <span>{new Date(post.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => window.open(`/blog/${post._id}`, '_blank')}
                    className="p-2 text-[#9CB0C7] hover:text-white transition-colors"
                    title="Preview"
                  >
                    <Eye className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleEditPost(post)}
                    className="p-2 text-[#3EF1C7] hover:text-white transition-colors"
                    title="Edit"
                  >
                    <Edit className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDeletePost(post._id)}
                    className="p-2 text-red-500 hover:text-red-400 transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
